import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAdminUsers, type UserListParams } from '../lib/api'
import { fmtDate } from '../lib/format'

export function UserListPage() {
  const [params, setParams] = useState<UserListParams>({ page: 1, perPage: 50 })
  const [emailInput, setEmailInput] = useState('')
  const q = useAdminUsers(params)

  const applyFilters = (extra: Partial<UserListParams>) => {
    setParams((p) => ({ ...p, page: 1, ...extra }))
  }

  return (
    <>
      <header className="page-header">
        <div className="page-header__title-row">
          <h1 className="page-header__title">Users</h1>
          <span className="page-header__subtitle">Search, filter, and inspect trader / staff accounts.</span>
        </div>
      </header>

      <div className="filter-bar" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="filter-bar__group">
          <label className="field__label" htmlFor="user-email">Email</label>
          <input
            id="user-email"
            type="text"
            className="field__input"
            placeholder="email@example.com"
            style={{ width: 240 }}
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onBlur={() => applyFilters({ email: emailInput || undefined })}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilters({ email: emailInput || undefined }) }}
          />
        </div>
        <div className="filter-bar__group">
          <label className="field__label" htmlFor="user-role">Role</label>
          <select
            id="user-role"
            className="field__input"
            style={{ width: 160 }}
            value={params.role ?? ''}
            onChange={(e) => applyFilters({ role: e.target.value || undefined })}
          >
            <option value="">All</option>
            <option value="trader">trader</option>
            <option value="admin">admin</option>
            <option value="reviewer">reviewer</option>
            <option value="support">support</option>
            <option value="finance">finance</option>
            <option value="super_admin">super_admin</option>
          </select>
        </div>
        <div className="filter-bar__group">
          <label className="field__label" htmlFor="user-banned">Status</label>
          <select
            id="user-banned"
            className="field__input"
            style={{ width: 140 }}
            value={params.banned === undefined ? '' : params.banned ? 'banned' : 'active'}
            onChange={(e) => {
              const v = e.target.value
              applyFilters({ banned: v === '' ? undefined : v === 'banned' })
            }}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="banned">Banned</option>
          </select>
        </div>
      </div>

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Country</th>
              <th>Email verified</th>
              <th>Status</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</td></tr>
            )}
            {q.data && q.data.users.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>No matches.</td></tr>
            )}
            {q.data && q.data.users.map((u) => (
              <tr key={u.id}>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>{u.email}</td>
                <td>{u.displayName ?? '—'}</td>
                <td><span className="admin-pill admin-pill--info">{u.role}</span></td>
                <td>{u.countryCode ?? '—'}</td>
                <td>{u.emailVerifiedAt ? '✓' : '—'}</td>
                <td>
                  {u.bannedAt ? (
                    <span className="admin-pill admin-pill--danger">BANNED</span>
                  ) : (
                    <span className="admin-pill admin-pill--success">active</span>
                  )}
                </td>
                <td style={{ color: 'var(--text-secondary)' }}>{fmtDate(u.createdAt)}</td>
                <td style={{ textAlign: 'right' }}>
                  <Link to={`/users/${u.id}`} className="btn btn--secondary btn--sm">Details</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination" style={{ marginTop: 'var(--space-4)' }}>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          disabled={(params.page ?? 1) <= 1}
          onClick={() => setParams((p) => ({ ...p, page: Math.max(1, (p.page ?? 1) - 1) }))}
        >
          Previous
        </button>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', padding: '0 var(--space-3)' }}>
          Page {params.page ?? 1}
        </span>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
        >
          Next
        </button>
      </div>
    </>
  )
}
